import React, { useState, useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { db } from "../../../firebase";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Table,
  Spinner,
  Button,
  ProgressBar,
  Dropdown,
  Nav,
  Form,
  InputGroup,
  Alert
} from "react-bootstrap";
import {
  FaCarAlt,
  FaCogs,
  FaTachometerAlt,
  FaTools,
  FaRoad,
  FaArrowUp,
  FaArrowDown,
  FaSyncAlt,
  FaChartBar,
  FaWallet,
  FaClock,
  FaShoppingCart,
  FaMoneyBillWave,
  FaListAlt,
  FaCalendarAlt,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaEllipsisH,
  FaDownload,
  FaPrint,
  FaChartLine,
  FaFilter,
  FaSearch,
  FaInfoCircle,
  FaUserAlt,
  FaPercentage,
  FaExchangeAlt,
  FaSpinner,
  FaDollarSign,
  FaBell,
  FaStar,
  FaRegEnvelope,
  FaAngleRight,
  FaLightbulb,
  FaTrophy,
  FaChartPie
} from "react-icons/fa";
import AdminHeader from "../../../Components/AdminHeader";
import Sidebar from "../../../Components/Sidebar";
import RecentOrders from "../../../Components/RecentOrders";

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [period, setPeriod] = useState("weekly");
  const [reportType, setReportType] = useState("orders");
  const [activeCard, setActiveCard] = useState("balance");
  
  const barChartRef = useRef(null);
  const barChartInstance = useRef(null);
  const lineChartRef = useRef(null);
  const lineChartInstance = useRef(null);
  const pieChartRef = useRef(null);
  const pieChartInstance = useRef(null);

  // Fetch all orders from Firestore on mount
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const ordersQuery = query(
        collection(db, "Orders"), 
        orderBy("createdAt", "desc")
      );
      
      const querySnapshot = await getDocs(ordersQuery);
      const fetchedOrders = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Parse and sort dates
      const parseDate = (dateStr) => {
        if (!dateStr) return new Date(0);
        const [day, month, year] = dateStr.split("/");
        return new Date(year, month - 1, day);
      };

      fetchedOrders.sort((a, b) => {
        if (a.date && b.date) {
          return parseDate(b.date) - parseDate(a.date);
        }
        const aTime = a.createdAt ? new Date(a.createdAt.seconds * 1000) : new Date(0);
        const bTime = b.createdAt ? new Date(b.createdAt.seconds * 1000) : new Date(0);
        return bTime - aTime;
      });

      setOrders(fetchedOrders);
      setLastUpdated(new Date());
      setError(null);
    } catch (error) {
      console.error("Error fetching orders: ", error);
      setError("Failed to load dashboard data. Please try again.");
      toast.error("Error loading dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract the sale amount
  const getSaleAmount = (order) => {
    if (order.price && Array.isArray(order.price)) {
      const amount = order.price[0].toString().replace(/,/g, "");
      return parseFloat(amount) || 0;
    }
    return parseFloat(order.totalPrice) || 0;
  };

  // Filter orders by status
  const pendingOrders = orders.filter(
    (order) => order.status === "Pending" || !order.status
  );
  const completedOrders = orders.filter((order) => order.status === "Delivered");
  const cancelledOrders = orders.filter((order) => order.status === "Cancelled");

  const pendingCount = pendingOrders.length;
  const completedCount = completedOrders.length;
  const cancelledCount = cancelledOrders.length;
  const totalOrderNumber = orders.length;

  // Calculate total amounts for balance and each category
  const totalBalance = orders.reduce((sum, order) => sum + getSaleAmount(order), 0);
  const pendingSum = pendingOrders.reduce((sum, order) => sum + getSaleAmount(order), 0);
  const completedSum = completedOrders.reduce((sum, order) => sum + getSaleAmount(order), 0);
  const cancelledSum = cancelledOrders.reduce((sum, order) => sum + getSaleAmount(order), 0);

  // Create or update the bar chart
  useEffect(() => {
    if (loading || !orders.length) return;

    const ctx = document.getElementById("orderStatusChart");
    if (!ctx) return;

    // Destroy previous chart instance if it exists
    if (barChartInstance.current) {
      barChartInstance.current.destroy();
    }

    // Create new chart
    barChartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: ["Pending", "Completed", "Cancelled"],
        datasets: [
          {
            label: "Order Count",
            data: [pendingCount, completedCount, cancelledCount],
            backgroundColor: ["#f0ad4e", "#5cb85c", "#d9534f"],
            borderColor: ["#eea236", "#4cae4c", "#d43f3a"],
            borderWidth: 1,
            borderRadius: 4,
            barThickness: 30,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.7)",
            padding: 10,
            titleFont: {
              size: 14,
            },
            bodyFont: {
              size: 13,
            },
            callbacks: {
              label: function(context) {
                return `Orders: ${context.raw}`;
              }
            }
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(0,0,0,0.05)",
            },
            ticks: {
              precision: 0,
              font: {
                size: 11
              }
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: 11
              }
            }
          },
        },
      },
    });

    return () => {
      if (barChartInstance.current) {
        barChartInstance.current.destroy();
      }
    };
  }, [orders, pendingCount, completedCount, cancelledCount, loading]);

  // Create line chart for revenue trend
  useEffect(() => {
    if (loading || !orders.length) return;

    const ctx = document.getElementById("revenueTrendChart");
    if (!ctx) return;

    // Generate mock data for the revenue trend
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const currentMonth = new Date().getMonth();
    const labels = [];
    const currentYearData = [];
    const lastYearData = [];

    // Generate last 6 months of data
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      labels.push(months[monthIndex]);
      
      // Mock revenue data - in real app, you would aggregate from real orders
      currentYearData.push(Math.floor(Math.random() * 50000) + 20000);
      lastYearData.push(Math.floor(Math.random() * 40000) + 15000);
    }

    // Destroy previous chart instance if it exists
    if (lineChartInstance.current) {
      lineChartInstance.current.destroy();
    }

    // Create new chart
    lineChartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: labels,
        datasets: [
          {
            label: "This Year",
            data: currentYearData,
            borderColor: "#007bff",
            backgroundColor: "rgba(0, 123, 255, 0.1)",
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: "#007bff",
            pointRadius: 3,
            pointHoverRadius: 5,
          },
          {
            label: "Last Year",
            data: lastYearData,
            borderColor: "#6c757d",
            backgroundColor: "rgba(108, 117, 125, 0.05)",
            borderWidth: 2,
            borderDash: [5, 5],
            fill: true,
            tension: 0.4,
            pointBackgroundColor: "#6c757d",
            pointRadius: 3,
            pointHoverRadius: 5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            align: "end",
            labels: {
              boxWidth: 12,
              usePointStyle: true,
              pointStyle: "circle",
            },
          },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.7)",
            padding: 10,
            callbacks: {
              label: function(context) {
                return `${context.dataset.label}: $${context.raw.toLocaleString()}`;
              }
            }
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(0,0,0,0.05)",
            },
            ticks: {
              callback: function(value) {
                return '$' + value.toLocaleString();
              },
              font: {
                size: 11
              }
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: 11
              }
            }
          },
        },
      },
    });

    return () => {
      if (lineChartInstance.current) {
        lineChartInstance.current.destroy();
      }
    };
  }, [orders, loading]);

  // Create pie chart for order distribution
  useEffect(() => {
    if (loading || !orders.length) return;

    const ctx = document.getElementById("orderDistributionChart");
    if (!ctx) return;

    // Destroy previous chart instance if it exists
    if (pieChartInstance.current) {
      pieChartInstance.current.destroy();
    }

    // Create new chart
    pieChartInstance.current = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: ["Pending", "Completed", "Cancelled"],
        datasets: [
          {
            data: [pendingCount, completedCount, cancelledCount],
            backgroundColor: ["#f0ad4e", "#5cb85c", "#d9534f"],
            borderColor: ["#fff", "#fff", "#fff"],
            borderWidth: 2,
            hoverOffset: 10,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 12,
              padding: 15,
              usePointStyle: true,
              pointStyle: "circle",
            },
          },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.7)",
            padding: 10,
            callbacks: {
              label: function(context) {
                const percentage = ((context.raw / totalOrderNumber) * 100).toFixed(1);
                return `${context.label}: ${context.raw} (${percentage}%)`;
              }
            }
          },
        },
      },
    });

    return () => {
      if (pieChartInstance.current) {
        pieChartInstance.current.destroy();
      }
    };
  }, [orders, pendingCount, completedCount, cancelledCount, totalOrderNumber, loading]);

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Function to handle refresh data
  const handleRefresh = () => {
    fetchOrders();
    toast.info("Dashboard data refreshed");
  };

  // Calculate completion percentages
  const calculatePercentage = (completed, total) => {
    return Math.floor((completed / total) * 100);
  };

  // Get trend indicator
  const getTrendIndicator = (value, threshold = 0) => {
    if (value > threshold) {
      return (
        <span className="text-success d-inline-flex align-items-center ms-1">
          <FaArrowUp size={10} className="me-1" /> {value}%
        </span>
      );
    } else if (value < threshold) {
      return (
        <span className="text-danger d-inline-flex align-items-center ms-1">
          <FaArrowDown size={10} className="me-1" /> {Math.abs(value)}%
        </span>
      );
    }
    return (
      <span className="text-muted d-inline-flex align-items-center ms-1">
        {value}%
      </span>
    );
  };

  // Helper function to get status badge
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'delivered':
        return <Badge bg="success" pill>Completed</Badge>;
      case 'pending':
        return <Badge bg="warning" text="dark" pill>Pending</Badge>;
      case 'cancelled':
      case 'failed':
        return <Badge bg="danger" pill>Cancelled</Badge>;
      default:
        return <Badge bg="secondary" pill>Unknown</Badge>;
    }
  };

  return (
    <div className="admin-dashboard d-flex flex-column min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <AdminHeader />
      
      <div className="d-flex flex-grow-1">
        <Sidebar />
        
        <main className="flex-grow-1 bg-light">
          <Container fluid className="py-4">
            {/* Dashboard Header */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-1 fw-bold">Dashboard</h2>
                <p className="text-muted mb-0">
                  Welcome to Motor Easy Admin Panel
                </p>
              </div>
              <div className="d-flex mt-3 mt-md-0">
                <InputGroup className="me-2" style={{ width: "140px" }}>
                  <Form.Select 
                    size="sm"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </Form.Select>
                </InputGroup>
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={handleRefresh}
                  className="d-flex align-items-center"
                >
                  <FaSyncAlt className="me-2" />
                  Refresh
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading dashboard data...</p>
              </div>
            ) : error ? (
              <Alert variant="danger" className="d-flex align-items-center">
                <FaExclamationTriangle className="me-2" size={18} />
                <div>{error}</div>
              </Alert>
            ) : (
              <>
                {/* Quick Stats Summary */}
                <div className="bg-white rounded-3 shadow-sm mb-4 p-3">
                  <Row className="g-3">
                    <Col md={3} sm={6}>
                      <div className="d-flex align-items-center p-3">
                        <div className="rounded-3 bg-primary bg-opacity-10 p-3 me-3">
                          <FaShoppingCart className="text-primary" size={24} />
                        </div>
                        <div>
                          <h3 className="fw-bold mb-0">{totalOrderNumber}</h3>
                          <div className="text-muted small">Total Orders</div>
                          <div className="mt-1 small">
                            <span className={completedCount > pendingCount ? "text-success" : "text-danger"}>
                              {((completedCount / (totalOrderNumber || 1)) * 100).toFixed(1)}% complete rate
                            </span>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={3} sm={6}>
                      <div className="d-flex align-items-center p-3">
                        <div className="rounded-3 bg-success bg-opacity-10 p-3 me-3">
                          <FaDollarSign className="text-success" size={24} />
                        </div>
                        <div>
                          <h3 className="fw-bold mb-0">${totalBalance.toLocaleString()}</h3>
                          <div className="text-muted small">Total Revenue</div>
                          <div className="mt-1 small">
                            {getTrendIndicator(7.2)}
                            <span className="text-muted"> vs last period</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={3} sm={6}>
                      <div className="d-flex align-items-center p-3">
                        <div className="rounded-3 bg-warning bg-opacity-10 p-3 me-3">
                          <FaUserAlt className="text-warning" size={24} />
                        </div>
                        <div>
                          <h3 className="fw-bold mb-0">{Math.floor(orders.length * 0.8)}</h3>
                          <div className="text-muted small">Total Customers</div>
                          <div className="mt-1 small">
                            {getTrendIndicator(12.5)}
                            <span className="text-muted"> new customers</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={3} sm={6}>
                      <div className="d-flex align-items-center p-3">
                        <div className="rounded-3 bg-info bg-opacity-10 p-3 me-3">
                          <FaPercentage className="text-info" size={24} />
                        </div>
                        <div>
                          <h3 className="fw-bold mb-0">68.2%</h3>
                          <div className="text-muted small">Conversion Rate</div>
                          <div className="mt-1 small">
                            {getTrendIndicator(-2.4)}
                            <span className="text-muted"> vs last week</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Main Dashboard Content */}
                <Row className="g-4">
                  {/* Left Column */}
                  <Col lg={8}>
                    {/* Revenue Trend Chart */}
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <h5 className="mb-0 fw-bold text-primary">
                            <FaChartLine className="me-2" /> Revenue Trend
                          </h5>
                          <Badge bg="info" className="ms-2">
                            {period}
                          </Badge>
                        </div>
                        <div>
                          <Dropdown className="d-inline-block me-2">
                            <Dropdown.Toggle variant="light" size="sm" id="revenue-options">
                              <FaFilter className="me-1" /> Filter
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item active={reportType === "orders"} onClick={() => setReportType("orders")}>
                                Orders
                              </Dropdown.Item>
                              <Dropdown.Item active={reportType === "sales"} onClick={() => setReportType("sales")}>
                                Sales
                              </Dropdown.Item>
                              <Dropdown.Item active={reportType === "profits"} onClick={() => setReportType("profits")}>
                                Profits
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item onClick={() => handleRefresh()}>
                                Refresh Data
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>

                          <Button variant="outline-primary" size="sm">
                            <FaDownload className="me-1" /> Export
                          </Button>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <div style={{ height: "320px" }}>
                          <canvas id="revenueTrendChart"></canvas>
                        </div>
                      </Card.Body>
                    </Card>

                    {/* Order Status Overview */}
                    <Row className="mb-4">
                      <Col md={5}>
                        <Card className="border-0 shadow-sm h-100">
                          <Card.Header className="bg-white py-3">
                            <h5 className="mb-0 fw-bold text-primary">
                              <FaChartPie className="me-2" /> Order Distribution
                            </h5>
                          </Card.Header>
                          <Card.Body>
                            <div style={{ height: "240px", position: 'relative' }}>
                              <canvas id="orderDistributionChart"></canvas>
                              <div 
                                style={{ 
                                  position: 'absolute', 
                                  top: '50%', 
                                  left: '50%', 
                                  transform: 'translate(-50%, -50%)',
                                  textAlign: 'center'
                                }}
                              >
                                <h3 className="mb-0">{totalOrderNumber}</h3>
                                <div className="text-muted small">Total</div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={7}>
                        <Card className="border-0 shadow-sm h-100">
                          <Card.Header className="bg-white py-3">
                            <h5 className="mb-0 fw-bold text-primary">
                              <FaChartBar className="me-2" /> Order Status
                            </h5>
                          </Card.Header>
                          <Card.Body>
                            <Row className="text-center mb-3 g-2">
                              <Col sm={4}>
                                <div className="p-3 rounded-3 bg-warning bg-opacity-10">
                                  <h5 className="mb-0 text-warning">{pendingCount}</h5>
                                  <small className="text-muted">Pending</small>
                                </div>
                              </Col>
                              <Col sm={4}>
                                <div className="p-3 rounded-3 bg-success bg-opacity-10">
                                  <h5 className="mb-0 text-success">{completedCount}</h5>
                                  <small className="text-muted">Completed</small>
                                </div>
                              </Col>
                              <Col sm={4}>
                                <div className="p-3 rounded-3 bg-danger bg-opacity-10">
                                  <h5 className="mb-0 text-danger">{cancelledCount}</h5>
                                  <small className="text-muted">Cancelled</small>
                                </div>
                              </Col>
                            </Row>
                            <div style={{ height: "170px" }}>
                              <canvas id="orderStatusChart"></canvas>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    {/* Transaction History */}
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold text-primary">
                          <FaExchangeAlt className="me-2" /> Recent Transactions
                        </h5>
                        <div className="d-flex">
                          <InputGroup size="sm" className="me-2" style={{ width: "180px" }}>
                            <InputGroup.Text className="bg-white">
                              <FaSearch className="text-muted" />
                            </InputGroup.Text>
                            <Form.Control
                              placeholder="Search transactions..."
                              aria-label="Search transactions"
                            />
                          </InputGroup>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                          >
                            View All
                          </Button>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <Table hover responsive className="mb-0 table-borderless">
                          <thead className="bg-light">
                            <tr>
                              <th className="px-4 py-3">Date</th>
                              <th className="py-3">Transaction ID</th>
                              <th className="py-3">Description</th>
                              <th className="py-3">Amount</th>
                              <th className="px-4 py-3">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="px-4 py-3">
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle bg-light p-2 me-2">
                                    <FaCalendarAlt className="text-primary" size={14} />
                                  </div>
                                  <span>20 Jul 2022</span>
                                </div>
                              </td>
                              <td><code className="bg-light px-2 py-1 rounded-2">#TRX1001</code></td>
                              <td>Payment for Motor Parts</td>
                              <td className="fw-bold text-success">+$500.00</td>
                              <td className="px-4 py-3">
                                {getStatusBadge("completed")}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3">
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle bg-light p-2 me-2">
                                    <FaCalendarAlt className="text-primary" size={14} />
                                  </div>
                                  <span>19 Jul 2022</span>
                                </div>
                              </td>
                              <td><code className="bg-light px-2 py-1 rounded-2">#TRX1002</code></td>
                              <td>Refund for Service</td>
                              <td className="fw-bold text-danger">-$200.00</td>
                              <td className="px-4 py-3">
                                {getStatusBadge("failed")}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3">
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle bg-light p-2 me-2">
                                    <FaCalendarAlt className="text-primary" size={14} />
                                  </div>
                                  <span>18 Jul 2022</span>
                                </div>
                              </td>
                              <td><code className="bg-light px-2 py-1 rounded-2">#TRX1003</code></td>
                              <td>Payment for Vehicle Inspection</td>
                              <td className="fw-bold text-success">+$1,000.00</td>
                              <td className="px-4 py-3">
                                {getStatusBadge("pending")}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3">
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle bg-light p-2 me-2">
                                    <FaCalendarAlt className="text-primary" size={14} />
                                  </div>
                                  <span>17 Jul 2022</span>
                                </div>
                              </td>
                              <td><code className="bg-light px-2 py-1 rounded-2">#TRX1004</code></td>
                              <td>Payment for Subscription</td>
                              <td className="fw-bold text-success">+$50.00</td>
                              <td className="px-4 py-3">
                                {getStatusBadge("completed")}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </Card.Body>
                      <Card.Footer className="bg-white border-0 px-4 py-3 text-end">
                        <small className="text-muted">
                          Showing 4 of 125 transactions
                        </small>
                      </Card.Footer>
                    </Card>
                  </Col>

                  {/* Right Column */}
                  <Col lg={4}>
                    {/* Balance Card */}
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold text-primary">
                          <FaWallet className="me-2" /> Financial Summary
                        </h5>
                        <Dropdown>
                          <Dropdown.Toggle variant="light" size="sm" id="dropdown-basic">
                            <FaEllipsisH />
                          </Dropdown.Toggle>

                          <Dropdown.Menu align="end">
                            <Dropdown.Item href="#/action-1">
                              <FaDownload className="me-2" /> Export Report
                            </Dropdown.Item>
                            <Dropdown.Item href="#/action-2">
                              <FaPrint className="me-2" /> Print Statement
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item href="#/action-3">
                              <FaSyncAlt className="me-2" /> Refresh Data
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </Card.Header>
                      <Card.Body>
                        <div className="text-center mb-4">
                          <small className="text-uppercase text-muted fw-bold">Total Revenue</small>
                          <h2 className="mt-2 mb-0 display-5">${totalBalance.toLocaleString()}</h2>
                          <div className="d-flex align-items-center justify-content-center mt-2">
                            <Badge bg="success" className="d-flex align-items-center px-2 py-1">
                              <FaArrowUp size={10} className="me-1" /> 12.5%
                            </Badge>
                            <small className="text-muted ms-2">vs previous period</small>
                          </div>
                        </div>

                        <Nav 
                          variant="pills" 
                          className="nav-justified mb-3"
                          activeKey={activeCard}
                          onSelect={(key) => setActiveCard(key)}
                        >
                          <Nav.Item>
                            <Nav.Link eventKey="balance" className="py-2 px-1">
                              <div className="d-flex flex-column align-items-center">
                                <small>Balance</small>
                                <span className="fw-bold">${(totalBalance - pendingSum).toLocaleString()}</span>
                              </div>
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="earnings" className="py-2 px-1">
                              <div className="d-flex flex-column align-items-center">
                                <small>Earnings</small>
                                <span className="fw-bold text-success">${completedSum.toLocaleString()}</span>
                              </div>
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="pending" className="py-2 px-1">
                              <div className="d-flex flex-column align-items-center">
                                <small>Pending</small>
                                <span className="fw-bold text-warning">${pendingSum.toLocaleString()}</span>
                              </div>
                            </Nav.Link>
                          </Nav.Item>
                        </Nav>

                        <div className="mb-4">
                          <ProgressBar style={{ height: "8px" }} className="mb-2">
                            <ProgressBar variant="success" now={(completedSum / totalBalance) * 100} key={1} />
                            <ProgressBar variant="warning" now={(pendingSum / totalBalance) * 100} key={2} />
                            <ProgressBar variant="danger" now={(cancelledSum / totalBalance) * 100} key={3} />
                          </ProgressBar>
                          <div className="d-flex justify-content-between small">
                            <span className="text-success">Completed ({((completedSum / totalBalance) * 100).toFixed(1)}%)</span>
                            <span className="text-warning">Pending ({((pendingSum / totalBalance) * 100).toFixed(1)}%)</span>
                            <span className="text-danger">Cancelled ({((cancelledSum / totalBalance) * 100).toFixed(1)}%)</span>
                          </div>
                        </div>

                        <div className="d-grid gap-2 mt-4">
                          <Button variant="outline-primary" size="sm">
                            <FaDownload className="me-2" /> Download Full Report
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>

                    {/* Plannings Card */}
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold text-primary">
                          <FaListAlt className="me-2" /> Project Progress
                        </h5>
                        <Dropdown>
                          <Dropdown.Toggle variant="light" size="sm" id="dropdown-basic">
                            <FaEllipsisH />
                          </Dropdown.Toggle>

                          <Dropdown.Menu align="end">
                            <Dropdown.Item href="#/action-1">View All Projects</Dropdown.Item>
                            <Dropdown.Item href="#/action-2">Add New Project</Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item href="#/action-3">Generate Report</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <div className="list-group list-group-flush">
                          <div className="list-group-item px-4 py-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="d-flex align-items-center">
                                <div className="me-3 rounded-circle bg-primary bg-opacity-10 p-3">
                                  <FaCarAlt className="text-primary" />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-bold">New Model Launch</h6>
                                  <small className="text-muted">Dec 15, 2024</small>
                                </div>
                              </div>
                              <h6 className="mb-0 text-primary fw-bold">40%</h6>
                            </div>
                            <div className="d-flex justify-content-between align-items-center small text-muted mb-2">
                              <span>Budget: $150,000</span>
                              <span>Used: $50,000</span>
                            </div>
                            <ProgressBar variant="primary" now={40} className="mt-2" style={{ height: "6px" }} />
                          </div>

                          <div className="list-group-item px-4 py-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="d-flex align-items-center">
                                <div className="me-3 rounded-circle bg-success bg-opacity-10 p-3">
                                  <FaCogs className="text-success" />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-bold">Service Upgrade</h6>
                                  <small className="text-muted">Nov 30, 2024</small>
                                </div>
                              </div>
                              <h6 className="mb-0 text-success fw-bold">80%</h6>
                            </div>
                            <div className="d-flex justify-content-between align-items-center small text-muted mb-2">
                              <span>Budget: $30,000</span>
                              <span>Used: $24,000</span>
                            </div>
                            <ProgressBar variant="success" now={80} className="mt-2" style={{ height: "6px" }} />
                          </div>

                          <div className="list-group-item px-4 py-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="d-flex align-items-center">
                                <div className="me-3 rounded-circle bg-danger bg-opacity-10 p-3">
                                  <FaTachometerAlt className="text-danger" />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-bold">Performance Boost</h6>
                                  <small className="text-muted">Mar 10, 2025</small>
                                </div>
                              </div>
                              <h6 className="mb-0 text-danger fw-bold">20%</h6>
                            </div>
                            <div className="d-flex justify-content-between align-items-center small text-muted mb-2">
                              <span>Budget: $60,000</span>
                              <span>Used: $12,000</span>
                            </div>
                            <ProgressBar variant="danger" now={20} className="mt-2" style={{ height: "6px" }} />
                          </div>

                          <div className="list-group-item px-4 py-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="d-flex align-items-center">
                                <div className="me-3 rounded-circle bg-warning bg-opacity-10 p-3">
                                  <FaTools className="text-warning" />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-bold">Maintenance</h6>
                                  <small className="text-muted">Jan 5, 2025</small>
                                </div>
                              </div>
                              <h6 className="mb-0 text-warning fw-bold">60%</h6>
                            </div>
                            <div className="d-flex justify-content-between align-items-center small text-muted mb-2">
                              <span>Budget: $15,000</span>
                              <span>Used: $9,000</span>
                            </div>
                            <ProgressBar variant="warning" now={60} className="mt-2" style={{ height: "6px" }} />
                          </div>
                        </div>
                      </Card.Body>
                      <Card.Footer className="bg-white py-3 text-center">
                        <Button variant="outline-primary" size="sm">
                          View All Projects <FaAngleRight className="ms-1" />
                        </Button>
                      </Card.Footer>
                    </Card>

                    {/* Quick Stats */}
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Header className="bg-white py-3">
                        <h5 className="mb-0 fw-bold text-primary">
                          <FaInfoCircle className="me-2" /> Quick Insights
                        </h5>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <div className="list-group list-group-flush">
                          <div className="list-group-item px-4 py-3 d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle bg-success bg-opacity-10 p-2 me-3">
                                <FaStar className="text-success" />
                              </div>
                              <div>
                                <h6 className="mb-0">Customer Satisfaction</h6>
                                <small className="text-muted">Based on reviews</small>
                              </div>
                            </div>
                            <h5 className="mb-0 fw-bold">4.8/5</h5>
                          </div>
                          <div className="list-group-item px-4 py-3 d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-3">
                                <FaBell className="text-primary" />
                              </div>
                              <div>
                                <h6 className="mb-0">Pending Notifications</h6>
                                <small className="text-muted">Requires attention</small>
                              </div>
                            </div>
                            <h5 className="mb-0 fw-bold">7</h5>
                          </div>
                          <div className="list-group-item px-4 py-3 d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle bg-warning bg-opacity-10 p-2 me-3">
                                <FaRegEnvelope className="text-warning" />
                              </div>
                              <div>
                                <h6 className="mb-0">Unread Messages</h6>
                                <small className="text-muted">From customers</small>
                              </div>
                            </div>
                            <h5 className="mb-0 fw-bold">13</h5>
                          </div>
                          <div className="list-group-item px-4 py-3 d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle bg-info bg-opacity-10 p-2 me-3">
                                <FaTrophy className="text-info" />
                              </div>
                              <div>
                                <h6 className="mb-0">Monthly Goal</h6>
                                <small className="text-muted">Sales progress</small>
                              </div>
                            </div>
                            <h5 className="mb-0 fw-bold">78%</h5>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>

                    {/* Tips Card */}
                    <Card className="border-0 shadow-sm mb-4 bg-primary bg-opacity-10">
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-circle bg-primary bg-opacity-25 p-2 me-3">
                            <FaLightbulb className="text-primary" size={20} />
                          </div>
                          <h5 className="mb-0 fw-bold text-primary">Pro Tips</h5>
                        </div>
                        <p className="mb-0">
                          Did you know you can export your reports as CSV, Excel or PDF? Click the export button on any report to download it.
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Recent Orders Component */}
                <Card className="border-0 shadow-sm mb-4">
                  <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold text-primary">
                      <FaShoppingCart className="me-2" /> Recent Orders
                    </h5>
                    <Button variant="outline-primary" size="sm">
                      View All Orders
                    </Button>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <RecentOrders />
                  </Card.Body>
                </Card>
              </>
            )}
          </Container>
        </main>
      </div>
    </div>
  );
};
export default Dashboard;